import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import * as ApolloWrappers from 'graphql-client/ApolloWrappers';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import type {
  LibraryParameterDefinitionTableDescriptionQuery,
  ProcedureParameterValidateUnitTypeQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';

import type {
  GetLibraryParameterDefinitionForTableColumnQuery,
  GetLibraryParameterDefinitionForTableColumnQueryVariables,
} from './__generated/GetLibraryParameterDefinitionForTableColumnQuery';
import type {
  ValidateUnitTypeForLibraryParameterDefinitionQuery,
  ValidateUnitTypeForLibraryParameterDefinitionQueryVariables,
} from './__generated/ValidateUnitTypeForLibraryParameterDefinitionQuery';

const GET_LIBRARY_PARAMETER_DEFINITION_DESCRIPTION = gql`
  query GetLibraryParameterDefinitionForTableColumnQuery($id: StrictString!)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    libraryParameterDefinition(id: $id) {
      id
      description
    }
  }
`;

export async function resolveLibraryParameterDefinitionDescriptionFromId(
  query: LibraryParameterDefinitionTableDescriptionQuery
): Promise<ServerResolutionResult> {
  const {libraryParameterDefinitionId} = query;

  const result = await ApolloWrappers.doQueryWithoutCache<
    GetLibraryParameterDefinitionForTableColumnQuery,
    GetLibraryParameterDefinitionForTableColumnQueryVariables
  >({query: GET_LIBRARY_PARAMETER_DEFINITION_DESCRIPTION}, {variables: {id: libraryParameterDefinitionId}});
  const definition = result.libraryParameterDefinition;

  return makeCustomResolutionResultFromTypedValue({
    type: BaseTypedValueType.STRING,
    rawValue: definition.description ?? '',
  });
}

const VALIDATE_UNIT_TYPE_FOR_LIBRARY_PARAMETER_DEFINITION = gql`
  query ValidateUnitTypeForLibraryParameterDefinitionQuery(
    $libraryParameterDefinitionId: StrictString!
    $unitIds: [StrictString!]!
  ) @skipAudit @engComponent(name: PROCEDURES) {
    libraryParameterDefinition(id: $libraryParameterDefinitionId) {
      id
      unitType {
        ... on UnitTypeNode {
          id
          name
        }
      }
    }
    units(ids: $unitIds) {
      ... on UnitNode {
        id
        unitType {
          ... on UnitTypeNode {
            id
          }
        }
      }
    }
  }
`;

export async function validateLibraryParameterDefinitionUnitType(
  query: ProcedureParameterValidateUnitTypeQuery
): Promise<ServerResolutionResult> {
  const {libraryParameterDefinitionId, unitId} = query;

  const result = await ApolloWrappers.doQueryWithoutCache<
    ValidateUnitTypeForLibraryParameterDefinitionQuery,
    ValidateUnitTypeForLibraryParameterDefinitionQueryVariables
  >(
    {query: VALIDATE_UNIT_TYPE_FOR_LIBRARY_PARAMETER_DEFINITION},
    // Query unitIds rather than unitId so we can handle the non-null and null unit case within the same query.
    // We still need to run the query in the null unit case to get the library parameter definition unit type
    // because a null unit with a non-null unit type is invalid.
    {variables: {libraryParameterDefinitionId, unitIds: unitId != null ? [unitId] : []}}
  );

  const libraryUnitType = result.libraryParameterDefinition.unitType;

  // If the library definition has no unit type restriction, any unit is valid.
  if (libraryUnitType == null) {
    return makeCustomResolutionResultFromTypedValue(null);
  }

  if (unitId == null) {
    return makeCustomResolutionResultFromTypedValue(null, 'Unit is required');
  }

  const unit = result.units.find((u) => u.id === unitId);
  assert(unit != null, `Unit not found: ${unitId}`);

  if (unit.unitType.id === libraryUnitType.id) {
    return makeCustomResolutionResultFromTypedValue(null);
  }

  return makeCustomResolutionResultFromTypedValue(null, `Unit must be of type "${libraryUnitType.name}"`);
}
